/** Soft≠Live Page146 — AI Policy residual (runtime bus).
 * Force analyze · interrupt queue · predictive.
 * Hold/Allow = human interrupt (SoT 2026-08-08), not permanent hard gate.
 */
import { useState } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, Sec, go } from "./ui.js";
import { path, useAction, useApi, usePolled, api } from "./api.js";
import { appendLocalEvidence } from "./ai-local-evidence.js";
import { holdTeamRunOrRefuse, continueTeamRunPrefer, continueRefuseToast, holdRefuseToast } from "./hold-team-runtime.js";
import { envelopeSummary, normalizeActionEnvelope } from "./action-envelope.js";

/** Path-free operator copy for AI runtime policy. */
export function softAiPolicyErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|denied|insufficient/.test(low)) {
    return "Your account cannot run this AI action. Ask a manager if you need access.";
  }
  if (/\b401\b|unauthorized|session/.test(low)) return "Sign in again to continue.";
  if (/\b422\b|\b400\b|invalid|required/.test(low)) {
    return "That AI action was not accepted. Check the fields and try again.";
  }
  if (/\/api\/|\/ai\/|GET |POST |PATCH |http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}


/** Pure: normalize pending interrupt rows. */
export function pendingInterruptRows(pack) {
  const root = pack || {};
  const rows = root.items || root.incidents || root.rows || [];
  if (!Array.isArray(rows)) return [];
  return rows.filter((it) => {
    const st = String((it && it.status) || "").toLowerCase();
    return !st || st === "pending" || st === "open";
  });
}

/** Pure: advisory rows from predictive maintenance envelope. */
export function predictiveRowsOf(pack) {
  const root = pack || {};
  const rows = root.recommendations || root.items || root.advisories || root.devices || root.rows || [];
  return Array.isArray(rows) ? rows : [];
}

export function AiPolicyLive({ toast, settingsEnabled, runtimeAllowed, predictiveReadAllowed }) {
  const runtimeOpen = runtimeAllowed != null ? !!runtimeAllowed : !!settingsEnabled;
  const liveOn = !!(settingsEnabled && runtimeOpen);
  const predictiveOn = predictiveReadAllowed === true;
  const { run, busy, error, envelope } = useAction();
  const pending = usePolled(
    liveOn ? path("get__ai_incidents", { query: { status: "pending" } }) : null,
    15000,
  );
  const predictive = useApi(
    predictiveOn ? path("get__predictive_maintenance") : null,
  );
  const [alarmId, setAlarmId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [eventId, setEventId] = useState("");
  const [eventType, setEventType] = useState("alarm.trigger");
  const [lastAnalyze, setLastAnalyze] = useState(null);
  const [sideEnvelope, setSideEnvelope] = useState(null);
  const lastActionEnv = envelope || sideEnvelope;

  const waiting = pendingInterruptRows(pending.data);
  const advisories = predictiveRowsOf(predictive.data);

  const forceAnalyze = async () => {
    if (!runtimeOpen) {
      toast && toast("Force analyze is not available on this seat.", "warn");
      return;
    }
    const aid = alarmId.trim();
    const did = deviceId.trim();
    const eid = Number(eventId);
    if (!aid || !did || !Number.isFinite(eid) || eid < 1) {
      toast("Need an alarm, a board, and an event number.", "warn");
      return;
    }
    const out = await run(path("post__ai_analyze"), {
      body: {
        alarm_id: aid,
        device_id: did,
        event_id: eid,
        event_type: eventType.trim() || "alarm.trigger",
      },
    });
    if (!out) {
      toast(softAiPolicyErrorOf(error) || "Analyze was refused", "bad");
      return;
    }
    setLastAnalyze(out);
    try {
      appendLocalEvidence("operation", {
        incidentId: String((out && (out.incident_id || (out.record && out.record.incident_id))) || aid),
        verb: "analyze",
        payload: {
          alarm_id: aid,
          device_id: did,
          event_id: eid,
          status: out && out.record && out.record.status,
          // never send to server from this pack
        },
      });
    } catch (_) {}
    toast("Analyze started · " + (out.incident_id || out.id || aid), "go");
    if (pending.reload) pending.reload();
  };

  const resolveInterrupt = async (id, approved) => {
    if (!runtimeOpen) {
      toast && toast("Interrupt handling is not available on this seat.", "warn");
      return;
    }
    if (!id) return;
    if (!approved) {
      // P3 Hold SoT Soft≠ invent — Team Runtime only; never AI Incident approve=false.
      const rows = pendingInterruptRows(pending.data);
      const hit = rows.find((r) => String(r.id || r.incident_id || "") === String(id));
      const hold = await holdTeamRunOrRefuse(api, path, id, {
        team_run_id: hit && (hit.team_run_id || hit.teamRunId),
        trigger: "ai_policy_interrupt_hold",
      });
      if (!hold.ok) {
        setSideEnvelope(
          normalizeActionEnvelope(hold.out, {
            actor_kind: "Human",
            error: hold.reason || "hold_refused",
          }),
        );
        toast(
          holdRefuseToast(hold.reason),
          "warn",
        );
        return;
      }
      setSideEnvelope(normalizeActionEnvelope(hold.out || { ok: true }, { actor_kind: "Human" }));
      try {
        appendLocalEvidence("operation", {
          incidentId: String(id),
          verb: "hold",
          payload: {
            hold_via: "team_runtime",
            soft_invent: false,
            server_ack: true,
          },
        });
      } catch (_) {}
      toast("Held · Team Runtime · " + id, "warn");
      if (pending.reload) pending.reload();
      return;
    }
    // P3 Continue Soft≠ invent — prefer Team Runtime when TeamRun exists.
    const rows = pendingInterruptRows(pending.data);
    const hit = rows.find((r) => String(r.id || r.incident_id || "") === String(id));
    const cont = await continueTeamRunPrefer(api, path, id, {
      team_run_id: hit && (hit.team_run_id || hit.teamRunId),
      trigger: "ai_policy_interrupt_continue",
    });
    if (cont.ok) {
      setSideEnvelope(normalizeActionEnvelope(cont.out || { ok: true }, { actor_kind: "Human" }));
      try {
        appendLocalEvidence("response", {
          incidentId: String(id),
          verb: "continue",
          payload: { continue_via: "team_runtime", soft_invent: false, server_ack: true },
        });
      } catch (_) {}
      toast("Continued · Team Runtime · " + id, "go");
      if (pending.reload) pending.reload();
      return;
    }
    toast(
      continueRefuseToast(cont.reason),
      "warn",
    );
  };

  if (!settingsEnabled || !runtimeOpen) {
    return html`<${Alert} tone="info" title="Runtime seat sealed"
      say="Force analyze, the interrupt queue, and predictive advisories stay closed until your runtime seat opens."/>`;
  }

  return html`<div className="ai-policy-live ai-policy-live--liquid" data-cmc-page="page146-ai-policy" data-cmc-runtime="page206-open">
    <${F}>
    <${Alert} tone="info" title="Runtime ops · under Policy Kernel"
      say="Exec follows Policy Kernel. Hold is human interrupt. Operation and response evidence packs stay on this device; server keeps thin audit."/>
    ${lastActionEnv
      ? html`<${KV} k="Last action" v=${envelopeSummary(lastActionEnv) || "—"} tone=${lastActionEnv.ok ? "hi" : "warn"}/>`
      : null}

    <${Card} ai title="Force analyze" meta="runtime" cls="mt"
        sub="Mints a case · not Hold · not Policy Kernel. Does not invent notify. Soft empty success is forbidden.">
      <${Field} label="Alarm id">
        <${Input} icon="alert" value=${alarmId} onChange=${e=>setAlarmId(e.target.value)}/>
      <//>
      <${Field} label="Device id">
        <${Input} icon="chip" value=${deviceId} onChange=${e=>setDeviceId(e.target.value)}/>
      <//>
      <${Field} label="Event id (numeric)">
        <${Input} icon="book" value=${eventId} onChange=${e=>setEventId(e.target.value)}/>
      <//>
      <${Field} label="Event type">
        <${Input} icon="brain" value=${eventType} onChange=${e=>setEventType(e.target.value)}/>
      <//>
      <button className="btn btn--go btn--sm" disabled=${busy} onClick=${forceAnalyze}
        style=${{ marginTop:8 }}>
        <${Ico} d="brain" w=${13}/> Run analyze</button>
      ${lastAnalyze ? html`<${Sec} title="Last analyze receipt">
        <p className="note">Incident ${String(lastAnalyze.incident_id || lastAnalyze.id || "—")} · status ${String((lastAnalyze.record && lastAnalyze.record.status) || lastAnalyze.status || "accepted")}</p>
      <//>` : null}
    <//>

    <${Card} title="Interrupt queue" meta="queue" cls="mt"
        sub="Plans waiting for a person. Continue or Hold. Not approve-every-step.">
      ${pending.loading && !pending.data
        ? html`<p className="note">Loading interrupts…</p>`
        : pending.error
          ? html`<${Alert} tone="warn" title="Interrupt queue unavailable" say=${softAiPolicyErrorOf(pending.error)}/>`
          : !waiting.length
            ? html`<p className="note">No pending interrupts — Sentinel is watching.</p>`
            : waiting.slice(0, 16).map((it, i) => {
                const id = String(it.incident_id || it.id || "");
                const risk = it.risk_score != null ? it.risk_score : it.risk;
                const independent = it.risk_independent === true;
                return html`<div key=${id || i} className="glass" style=${{ padding:"10px 12px", marginBottom:8, borderRadius:"var(--r-md)" }}>
                  <${KV} k="Incident" v=${id || "—"}/>
                  <${KV} k="Risk" v=${independent ? String(risk) : (risk != null ? "unproven" : "—")}
                    tone=${independent ? "go" : "warn"}/>
                  <${KV} k="Device" v=${String(it.device_id || "—")}/>
                  <div className="btn-row" style=${{ marginTop:8 }}>
                    <button className="btn btn--go btn--sm" disabled=${busy || !id}
                      onClick=${()=>resolveInterrupt(id, true)}>Continue</button>
                    <button className="btn btn--sm" disabled=${busy || !id}
                      onClick=${()=>resolveInterrupt(id, false)}>Hold</button>
                    <button className="btn btn--ghost btn--sm"
                      onClick=${()=>go("/response/cases")}>Open Mission Room</button>
                  </div>
                </div>`;
              })}
      <p className="note">${waiting.length} waiting · refreshes every 15s</p>
    <//>

    <${Card} title="Predictive maintenance" meta="advisories" cls="mt">
      ${!predictiveOn
        ? html`<p className="note">Maintenance advisories stay off on this deployment.</p>`
        : predictive.loading && !predictive.data
          ? html`<p className="note">Loading advisories…</p>`
          : predictive.error
            ? html`<${Alert} tone="warn" title="Predictive lane unavailable"
                say=${softAiPolicyErrorOf(predictive.error)}/>`
            : !advisories.length
              ? html`<p className="note">No advisories returned for this tenant.</p>`
              : advisories.slice(0, 12).map((a, i) => html`
                  <div key=${a.device_id || a.id || i} className="kv">
                    <span>${String(a.device_id || a.id || "device")}</span>
                    <b>${String(a.summary || a.message || a.severity || a.status || "advisory")}</b>
                  </div>`)}
      <div className="btn-row" style=${{ marginTop:8 }}>
        ${predictiveOn ? html`<button className="btn btn--ghost btn--sm" onClick=${()=>predictive.reload && predictive.reload()}>
          Refresh advisories</button>` : null}
        <button className="btn btn--ghost btn--sm" onClick=${()=>go("/response/readiness")}>
          Before tonight</button>
      </div>
    <//>
    <//>
  </div>`;
}
